import type { UseFormReturn } from 'react-hook-form';

import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field, Input } from '~/components/Form';

type FormWithTitle = {
  title: string;
};

type Props<T extends FormWithTitle> = {
  form: T extends FormWithTitle ? UseFormReturn<T> : never;
};

export const FormTitle = <T extends FormWithTitle>({ form: { control } }: Props<T>) => {
  const { t } = useTranslation(['invoices', 'validation']);

  return (
    <Controller
      control={control}
      name="title"
      render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label>{t('invoices:fields.title.label')}</Field.Label>

          <Input
            maxLength={240}
            onBlur={onBlur}
            onValueChange={onChange}
            placeholder={t('invoices:fields.title.placeholder')}
            ref={ref}
            value={value}
          />

          <Field.Error match={!!error}>{error?.message}</Field.Error>
        </Field.Root>
      )}
      rules={{
        maxLength: {
          value: 255,
          message: t('validation:maxLength', { attribute: t('invoices:fields.title.label'), max: 255 }),
        },
        required: { value: true, message: t('validation:required') },
        validate: (value) => !!value.trim() || t('validation:required'),
      }}
    />
  );
};
